import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import {
  StockOrderInformation,
  StockOrderProcessDeliveryRecieveApproval,
  StockOrderProcessNewOrder,
  StockOrderProcessStoreRecieveOrder,
  StockOrderProcessSupplierDispatchOrder,
  StockOrderProductInfo,
} from "../components";

import {
  getProductData,
  GetProductDataState,
  resetGetProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";
import { IoMdClose } from "react-icons/io";
import { useEffect, useRef } from "react";
import { AiOutlineDownload } from "react-icons/ai";
import { createQueryParams } from "features/config/helpers";
import { getStockOrderStores } from "../slices/get-store.slice";
import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { getDeliverySchedule } from "../slices/get-delivery-schedule.slice";
import {
  closeModalScroll,
  selectModalScroll,
} from "../slices/modalScrollSlice.slice";
import { useNavigate, useParams } from "react-router-dom";
import {
  getOrderComment,
  resetGetOrderComment,
  selectGetOrderComment,
} from "../slices/get-order-comment.slice";
import {
  closeStockProcessInfoEditButton,
  closeStockProcessProductEditButton,
  selectstockOrderGlobalTrigger,
} from "../slices/stock-order.slice";
import { resetGetStockOrderProductsStatus } from "../slices/get-products.slice";
import { CommentSection } from "features/shared/presentation/components";
import { postComment } from "../slices/post-comment.slice";
import { selectStockActiveProcess } from "../slices/stock-active-process.slice";
import { getStockOrders } from "../slices/get-stock-orders.slice";
import { useActionAvailability } from "../components/stock-ordering-utils";
import { RateOrderButton } from "../components/product-rating-button";
import {
  getBatchProductReviews,
  resetBatchProductReviews,
} from "../slices/batch-product-reviews.slice";

interface ProcessModalProps {
  open: boolean;
  onClose: () => void;
  payMultipleBilling?: boolean;
  scrollTarget?: string;
}

export function ProcessModal(props: ProcessModalProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { view, orderId } = useParams();

  const query = useQuery();
  const pageNo = query.get("page_no");
  const perPage = query.get("per_page");
  const orderBy = query.get("order_by");
  const order = query.get("order");

  const search = query.get("search");
  const status = query.get("status");
  const store = query.get("store");
  const tabValue = query.get("tab");

  const dateType = query.get("dateType");
  const startDate = query.get("startDate");
  const endDate = query.get("endDate");

  const commentPage = query.get("commentPage");

  const target = query.get("target");

  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const getProductDataState = useAppSelector(selectGetProductData);
  const modalScrollState = useAppSelector(selectModalScroll);
  const orderCommentState = useAppSelector(selectGetOrderComment);
  const stockActiveProcessState = useAppSelector(selectStockActiveProcess);
  const globalTriggerState = useAppSelector(selectstockOrderGlobalTrigger);
  const currentOrderStatusId =
    getProductDataState.data?.order_information.status_id;

  /**
   * Destructure comment data
   */
  const { commentData, pagination } = orderCommentState.data ?? {
    commentData: [],
  };

  /**
   * Destrucutre active process
   */
  const { process, process_action_access } = stockActiveProcessState.data ?? {};
  const { order_information, product_data } = getProductDataState.data ?? {};

  /**
   * Ref for autoscroll if comming from notification and is comment
   */
  const commentElementRef = useRef<HTMLDivElement>(null);

  const { processId } = useActionAvailability();

  const mainDivRef = useRef<HTMLDivElement>(null);
  const prevStatusRef = useRef(order_information?.status_id);

  useEffect(() => {
    if (props.open && product_data && orderId) {
      const productIds = product_data.map((product) => product.product_id);
      if (productIds.length > 0) {
        dispatch(getBatchProductReviews(productIds));
      }
    }
  }, [props.open, orderId, dispatch]);

  useEffect(() => {
    if (globalTriggerState.processClose) {
      handleCloseModal();
    }
  }, [globalTriggerState.processClose]);

  useEffect(() => {
    if (props.open && getProductDataState.data) {
      switch (target) {
        case "comment-section":
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (commentElementRef.current) {
                commentElementRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }, 300);
          });

          break;
      }
    }
  }, [props.open, getProductDataState.data, target]);

  useEffect(() => {
    if (orderId && props.open && !getProductDataState.data) {
      dispatch(getProductData({ orderId: orderId }));
      const query = createQueryParams({ orderId: orderId });
      dispatch(getOrderComment(query));
    }
  }, [dispatch, props.open, orderId, tabValue, getProductDataState.data]);

  useEffect(() => {
    const storeId = order_information?.store_id;
    if (tabValue === "0" && props.open && storeId) {
      const query = createQueryParams({
        store_id: storeId,
      });

      if (props.open) {
        dispatch(getStockOrderStores(query));
      }
    }
  }, [dispatch, order_information, tabValue, props.open]);

  const handleSubmitComment = (comment: string, reply: number | null) => {
    const { user_id } = getAdminSessionState.data?.admin ?? { user_id: null };

    dispatch(
      postComment({
        comment: comment,
        userId: user_id,
        orderInformationId: orderId ?? "",
        orderStatusId: Number(tabValue) + 1,
        replyToId: reply ?? null,
      })
    );
  };

  const handleDownloadTableData = () => {
    const link = `${REACT_APP_DOMAIN_URL}api/stock/export-order-pdf/${orderId}`;
    window.open(link, "_blank");

    handleCloseModal();
  };

  const handleCloseModal = () => {
    dispatch(resetGetProductData());
    dispatch(resetGetStockOrderProductsStatus());
    dispatch(closeStockProcessProductEditButton());
    dispatch(closeStockProcessInfoEditButton());
    dispatch(resetBatchProductReviews());

    const query = createQueryParams({
      page_no: pageNo,
      per_page: perPage,
      status: status,
      store: store,
      order_by: orderBy,
      order: order,
      search: search,
      dateType: dateType,
      startDate: startDate,
      endDate: endDate,
      tab: tabValue,
    });
    navigate({
      pathname: `/admin/material/${view}`,
      search: query,
    });

    dispatch(getStockOrders({ query: query, view, processId: processId }));

    document.body.classList.remove("overflow-hidden");
    props.onClose();
  };

  if (props.open) {
    mainDivRef.current?.focus();
    document.body.classList.add("overflow-hidden");
  } else {
    document.body.classList.remove("overflow-hidden");
    return null;
  }

  return (
    <div
      ref={mainDivRef}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleCloseModal();
          dispatch(closeModalScroll());
        }
      }}
      className={`fixed inset-0 z-30 flex items-start justify-center ${
        modalScrollState.status === false
          ? "overflow-auto bg-black bg-opacity-30 backdrop-blur-sm"
          : ""
      } `}
    >
      <div className="w-[97%] lg:w-[900px] my-5 rounded-[10px]">
        <div className="bg-secondary rounded-t-[10px] flex items-center justify-between p-4">
          <span className="text-2xl text-white uppercase">
            {
              process?.find(
                (status) => status.seqId - 1 === Number(tabValue) + 1
              )?.name
            }
          </span>

          <div className="space-x-3">
            <button
              className="text-2xl text-white"
              onClick={handleDownloadTableData}
            >
              <AiOutlineDownload />
            </button>

            <button className="text-2xl text-white" onClick={handleCloseModal}>
              <IoMdClose />
            </button>
          </div>
        </div>

        <div className="p-4 bg-paper border-b-2 border-l-2 border-r-2 border-secondary space-y-5 select-none">
          <StockOrderInformation />
          <StockOrderProductInfo
            onRatingClick={(productId: string) => {
              handleCloseModal();
              navigate(`/admin/material/${view}/reviews/${productId}`);
            }}
          />

          <section ref={commentElementRef}>
            <CommentSection
              commentData={commentData ?? []}
              onCommentSubmit={handleSubmitComment}
              currentPage={commentPage ? parseInt(commentPage) : 1}
              commentMax={pagination?.totalComments}
              commentPerPage={pagination?.commentPerPage ?? 25}
              onRowsPerPageChange={(event) => {
                if (pagination?.commentPage.toString() !== event.target.value) {
                  dispatch(resetGetOrderComment());
                  const query = createQueryParams({
                    commentPage: pagination?.commentPage,
                    commentPerPage: event.target.value,
                    orderId: orderId,
                  });

                  dispatch(getOrderComment(query));
                }
              }}
              onPageChange={(event, newPage) => {
                dispatch(resetGetOrderComment());
                const newCommmentPage = createQueryParams({
                  page_no: pageNo,
                  per_page: perPage,
                  status: status,
                  store: store,
                  order_by: orderBy,
                  order: order,
                  search: search,
                  dateType: dateType,
                  startDate: startDate,
                  endDate: endDate,
                  tab: tabValue,
                  commentPage: newPage,
                });

                navigate({
                  pathname: `/admin/material/${view}`,
                  search: newCommmentPage,
                });

                const pageNum = newPage ? newPage : 1;

                const query = createQueryParams({
                  commentPage: pageNum,
                  commentPerPage: pagination?.commentPerPage,
                  orderId: orderId,
                });
                dispatch(getOrderComment(query));
              }}
            />
          </section>

          <StockOrderProcessActionEnabler
            actionSequence={order_information?.status_id}
            onClose={handleCloseModal}
          />
          {order_information?.status_id === 10 && (
            <div className="flex justify-end p-4">
              <RateOrderButton
                statusId={currentOrderStatusId || 0}
                view={view}
                onCloseModal={props.onClose}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StockOrderProcessActionEnabler({
  actionSequence,
  onClose,
}: {
  actionSequence: number | undefined;
  onClose: () => void;
}) {
  const getProductDataState = useAppSelector(selectGetProductData);

  const { product_batch_data } = getProductDataState.data ?? {};

  const renderContent = () => {
    switch (actionSequence) {
      case 1:
        return <StockOrderProcessNewOrder onClose={onClose} />;
      case 4:
        return <StockOrderProcessSupplierDispatchOrder onClose={onClose} />;
      case 5:
        return <StockOrderProcessStoreRecieveOrder onClose={onClose} />;
      case 6:
        return <StockOrderProcessDeliveryRecieveApproval onClose={onClose} />;
      default:
        return (
          <div className="text-gray-500 text-sm">
            No process available for this step.
          </div>
        );
    }
  };

  return (
    <div className="border rounded-2xl shadow-sm p-5 bg-white">
      <div className="mt-3">{renderContent()}</div>
    </div>
  );
}
